import React, {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useState,
} from "react";
import {
  Checkbox,
  createStyles,
  ScrollArea,
  Table,
  MantineNumberSize,
} from "@mantine/core";

export const useDirectoryTableStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    backgroundColor: "#ffffff",
    transition: "box-shadow 150ms ease",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "Inter",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "0.16px",
    textAlign: "left",
    color: "var(--grey-grey-80)",
    width: "100%",

    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: `1px solid ${
        theme.colorScheme === "dark"
          ? theme.colors.dark[3]
          : theme.colors.gray[2]
      }`,
    },
  },

  scrolled: {
    boxShadow: theme.shadows.sm,
    zIndex: 1,
  },
  rowLabel: {
    fontSize: "14px",
    fontWeight: "normal",
    fontFamily: "Inter",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "1.15",
    letterSpacing: "normal",
    textAlign: "left",
    color: "var(--grey-grey-80)",
    width: "100%",
  },
}));

interface DirectoryTableInterface<ObjectType> {
  data: ObjectType[];
  tableVerticalSpacing?: MantineNumberSize;
  tableHorizontalSpacing?: MantineNumberSize;
  selection: ObjectType[];
  onSelect: React.Dispatch<React.SetStateAction<ObjectType[]>>;
  hideSelectControls?: boolean;
  isSelectable?: (item: ObjectType) => boolean;
  headers: any[];
  rows: {
    [key: string]: string | ReactNode | number;
  }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headersStyles?: any;
  tableMaxHeight?: string;
  loadingRows?: ReactNode;
}

const DirectoryTable = <ObjectType extends { id: number }>({
  data,
  selection,
  onSelect,
  headers,
  rows = [], // Provide default value
  hideSelectControls,
  isSelectable,
  tableVerticalSpacing,
  tableHorizontalSpacing,
  headersStyles,
  tableMaxHeight,
  loadingRows,
}: PropsWithChildren<DirectoryTableInterface<ObjectType>>): ReactElement => {
  const { classes, cx } = useDirectoryTableStyles();
  const [scrolled, setScrolled] = useState(false);

  const $selection = selection.map((it) => it.id);

  const toggleRow = (selectedItem: ObjectType): void =>
    onSelect((current) =>
      current.map((it) => it.id).includes(selectedItem.id)
        ? current.filter((item) => item.id !== selectedItem.id)
        : [...current, selectedItem]
    );

  const selectableData = isSelectable ? data.filter(isSelectable) : data;
  const allSelectableSelected =
    selectableData.length > 0 &&
    selectableData.every((item) => $selection.includes(item.id));

  const toggleAll = (): void =>
    onSelect(() =>
      allSelectableSelected ? [] : selectableData
    );

  const getTableHeaders = (): JSX.Element => {
    const extraHeaderStyles = headersStyles || {};
    if (hideSelectControls) {
      return (
        <tr>
          {headers.map((it) => (
            <th
              style={{
                border: "none",
                padding: "20px 10px",
                whiteSpace: "nowrap",
                ...extraHeaderStyles[it],
              }}
              key={`tHead-${it}`}
            >
              {it}
            </th>
          ))}
        </tr>
      );
    }
    return (
      <tr>
        <th style={{ width: 40, border: "none" }}>
          <Checkbox
            radius="sm"
            onChange={toggleAll}
            checked={allSelectableSelected}
            indeterminate={
              selection.length > 0 && !allSelectableSelected
            }
            transitionDuration={0}
          />
        </th>
        {headers.map((it) => (
          <th
            style={{
              whiteSpace: "nowrap",
              border: "none",
              padding: "20px 10px",
              ...extraHeaderStyles[it],
            }}
            key={`tHead-${it}`}
          >
            {it}
          </th>
        ))}
      </tr>
    );
  };

  const getTableBody = (): JSX.Element[] => {
    if (hideSelectControls) {
      return data.map((it, index) => (
        <tr key={it.id} className={cx("table-row")}>
          {Object.keys(rows[index] || {}).map((row) => (
            <td key={`tRow-${row}`} style={{ border: "none", width: "50px" }}>
              {rows[index][row]}
            </td>
          ))}
        </tr>
      ));
    }
    return data.map((it, index) => {
      const selected = $selection.includes(it.id);
      const itemSelectable = isSelectable ? isSelectable(it) : true;
      return (
        <tr key={it.id} className={cx("table-row")}>
          <td style={{ border: "none" }}>
            {itemSelectable ? (
              <Checkbox
                radius="sm"
                checked={selected}
                onChange={() => toggleRow(it)}
                transitionDuration={0}
              />
            ) : (
              <div style={{ width: 20, height: 20 }} />
            )}
          </td>

          {Object.keys(rows[index] || {}).map((row) => (
            <td key={`tRow-${row}`} style={{ border: "none" }}>
              {rows[index][row]}
            </td>
          ))}
        </tr>
      );
    });
  };

  return (
    <ScrollArea
      sx={{ height: tableMaxHeight || "75vh" }}
      scrollbarSize={0}
      onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
    >
      <Table
        highlightOnHover
        striped={!loadingRows}
        verticalSpacing={tableVerticalSpacing || "xs"}
        horizontalSpacing={tableHorizontalSpacing}
      >
        <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
          {getTableHeaders()}
        </thead>
        <tbody>{loadingRows ?? getTableBody()}</tbody>
      </Table>
    </ScrollArea>
  );
};

export default DirectoryTable;
